import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import CancelSaveButtons from '../../../components/common/CancelSaveButtons';
import { theme } from '../../../styles/theme';
import { convertToVwWeb } from '../../../Utils/responsive';
import { GIVING_ACCOUNT_MEMBER_TYPES, useGivingAccountApply } from '../useGivingAccountApply';

const GivingAccountApplyWeb = () => {
  const { t } = useTranslation('translation');
  const {
    step,
    memberType,
    isOrganization,
    handleSelectMemberType,
    agreement,
    agreementChecks,
    toggleAgreementCheck,
    canGoNext,
    handleNext,
    applicantName,
    applicantEmail,
    documents,
    organization,
    setOrganization,
    organizationDesc,
    setOrganizationDesc,
    canSubmit,
    handleRegisterDocument,
    handleCancel,
    handleSubmit,
  } = useGivingAccountApply();

  // 1단계 유형 선택 → 2단계 설명·동의 → 3단계 정보 입력. 1·2단계는 "다음", 3단계는 "신청하기"
  const isLastStep = step === 3;
  const primaryLabel = isLastStep ? t('page.givingAccountApply.actions.submit') : t('common.actions.next');
  const primaryDisabled = isLastStep ? !canSubmit : !canGoNext;
  const handlePrimary = isLastStep ? handleSubmit : handleNext;

  return (
    <WebPageLayout title={t('page.givingAccountApply.pageTitle')}>
      <PageContainer>
        <ContentWrapper>
          <FormCardWithFooter>
            <SectionContainer>
              <StepIndicator>{t('page.givingAccountApply.stepIndicator', { step, total: 3 })}</StepIndicator>
              <FieldList>
                {step === 1 && (
                  <>
                    <FieldGroup>
                      <FieldTitle>{t('page.givingAccountApply.steps.type')}</FieldTitle>
                      <RadioGroup role="radiogroup" $vertical>
                        {GIVING_ACCOUNT_MEMBER_TYPES.map((type) => (
                          <RadioItem
                            key={type}
                            role="radio"
                            aria-checked={memberType === type}
                            onClick={() => handleSelectMemberType(type)}
                          >
                            <RadioCircle active={memberType === type} />
                            <RadioLabel>{t(`page.givingAccountApply.accountTypes.${type}`)}</RadioLabel>
                          </RadioItem>
                        ))}
                      </RadioGroup>
                    </FieldGroup>
                  </>
                )}

                {step === 2 && (
                  <>
                    {agreement && (
                      <>
                        <FieldGroup>
                          <FieldTitle>{agreement.title}</FieldTitle>
                          <AgreementIntro>{agreement.intro}</AgreementIntro>
                        </FieldGroup>
                        <AgreementBox>
                          {agreement.sections.map((section) => (
                            <AgreementItem key={section.title}>
                              <AgreementItemTitle>{section.title}</AgreementItemTitle>
                              {section.body && <AgreementItemBody>{section.body}</AgreementItemBody>}
                              {section.items && (
                                <AgreementList>
                                  {section.items.map((item) => (
                                    <AgreementListItem key={item}>{item}</AgreementListItem>
                                  ))}
                                </AgreementList>
                              )}
                              {section.note && <AgreementNote>{section.note}</AgreementNote>}
                            </AgreementItem>
                          ))}
                        </AgreementBox>
                        <FieldGroup>
                          <FieldTitle>{agreement.checksTitle}</FieldTitle>
                          {agreement.checks.map((check, index) => (
                            <CheckboxGroup
                              key={check}
                              role="checkbox"
                              aria-checked={!!agreementChecks[index]}
                              onClick={() => toggleAgreementCheck(index)}
                            >
                              <CheckboxSquare active={!!agreementChecks[index]} />
                              <CheckboxLabel>{check}</CheckboxLabel>
                            </CheckboxGroup>
                          ))}
                        </FieldGroup>
                      </>
                    )}
                  </>
                )}

                {step === 3 && (
                  <>
                    <FieldGroup>
                      <FieldTitle>{t('page.givingAccountApply.steps.form')}</FieldTitle>
                      <InputBox disabled>
                        <InputLabel>{t('page.givingAccountApply.labels.memberType')}</InputLabel>
                        <InputValue>{memberType ? t(`page.givingAccountApply.accountTypes.${memberType}`) : '-'}</InputValue>
                      </InputBox>
                    </FieldGroup>

                    <InputGrid>
                      <InputBox disabled>
                        <InputLabel>{t('page.givingAccountApply.labels.applicantName')}</InputLabel>
                        <InputValue>{applicantName || '-'}</InputValue>
                      </InputBox>
                      <InputBox disabled>
                        <InputLabel>{t('page.givingAccountApply.labels.applicantEmail')}</InputLabel>
                        <InputValue>{applicantEmail || '-'}</InputValue>
                      </InputBox>
                    </InputGrid>

                    <FieldGroup>
                      <FieldTitle>{t('page.givingAccountApply.labels.documents')}</FieldTitle>
                      {documents.map((doc) => (
                        <DocumentRow key={doc.key}>
                          <DocumentName>{t(`page.givingAccountApply.documents.${doc.key}`)}</DocumentName>
                          {doc.registered ? (
                            <DocumentRegistered>{t('page.givingAccountApply.documents.registered')}</DocumentRegistered>
                          ) : (
                            <DocumentRegisterButton type="button" onClick={() => handleRegisterDocument(doc.key)}>
                              {t('page.givingAccountApply.documents.register')}
                            </DocumentRegisterButton>
                          )}
                        </DocumentRow>
                      ))}
                    </FieldGroup>

                    {isOrganization && (
                      <>
                        <InputBox as="label">
                          <InputLabel as="span">{t('page.givingAccountApply.labels.organization')}</InputLabel>
                          <StyledInput
                            type="text"
                            value={organization}
                            maxLength={100}
                            placeholder={t('page.givingAccountApply.placeholders.organization')}
                            onChange={(e) => setOrganization(e.target.value)}
                          />
                        </InputBox>

                        <InputBox as="label">
                          <InputLabel as="span">{t('page.givingAccountApply.labels.organizationDesc')}</InputLabel>
                          <StyledTextArea
                            value={organizationDesc}
                            rows={5}
                            maxLength={2000}
                            placeholder={t('page.givingAccountApply.placeholders.organizationDesc')}
                            onChange={(e) => setOrganizationDesc(e.target.value)}
                          />
                        </InputBox>
                      </>
                    )}
                  </>
                )}
              </FieldList>
            </SectionContainer>
            <ButtonGroup>
              <CancelSaveButtons
                platform="web"
                onCancel={handleCancel}
                onSave={handlePrimary}
                disabled={primaryDisabled}
                saveLabel={primaryLabel}
              />
            </ButtonGroup>
          </FormCardWithFooter>
        </ContentWrapper>
      </PageContainer>
    </WebPageLayout>
  );
};

/* 나의 계정(MyAccount) 웹 화면과 같은 카드·입력칸 스타일 */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
  align-items: center;
  padding-top: 24px;
  padding-bottom: ${convertToVwWeb(100)};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
  width: 100%;
  max-width: ${convertToVwWeb(892)};
  min-width: 0;
  box-sizing: border-box;
`;

const FormCardWithFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const SectionContainer = styled.div`
  border-radius: ${convertToVwWeb(12)} ${convertToVwWeb(12)} 0 0;
  border: 1px solid ${theme.colors.grayScale[20]};
  border-bottom: none;
  background: ${theme.colors.grayScale[0]};
  padding: ${convertToVwWeb(32)} ${convertToVwWeb(20)} ${convertToVwWeb(52)};
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(36)};
  width: 100%;
  max-width: ${convertToVwWeb(742)};
`;

const StepIndicator = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  margin: 0 0 ${convertToVwWeb(16)};
  width: 100%;
  max-width: ${convertToVwWeb(742)};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(12)};
  width: 100%;
`;

const FieldTitle = styled.p`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${convertToVwWeb(36)} ${convertToVwWeb(32)};
  width: 100%;
`;

const InputBox = styled.div<{ disabled?: boolean }>`
  background-color: ${(props) => (props.disabled ? theme.colors.grayScale[10] : 'white')};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwWeb(4)};
  padding: ${convertToVwWeb(8)} ${convertToVwWeb(12)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(2)};
  min-width: 0;
  box-sizing: border-box;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};

  &:focus-within {
    border: 1.6px solid ${theme.colors.primaryBlue[40]};
  }
`;

const InputLabel = styled.label`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
`;

const InputValue = styled.p`
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[50]};
  margin: 0;
  overflow-wrap: anywhere;
`;

const StyledInput = styled.input`
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[90]};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const StyledTextArea = styled.textarea`
  ${theme.typography.contents.regular20};
  font-family: inherit;
  color: ${theme.colors.grayScale[90]};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;
  resize: vertical;
  min-height: 150px;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const RadioGroup = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${({ $vertical }) => ($vertical ? 'column' : 'row')};
  gap: ${({ $vertical }) => ($vertical ? convertToVwWeb(16) : convertToVwWeb(35))};
  align-items: ${({ $vertical }) => ($vertical ? 'flex-start' : 'center')};
`;

const RadioItem = styled.div`
  display: flex;
  gap: ${convertToVwWeb(10)};
  align-items: center;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active?: boolean }>`
  width: ${convertToVwWeb(24)};
  height: ${convertToVwWeb(24)};
  border-radius: 50%;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwWeb(12)};
    height: ${convertToVwWeb(12)};
    border-radius: 50%;
    background: ${theme.colors.primaryBlue[40]};
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwWeb(12)};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwWeb(4)};
  padding: ${convertToVwWeb(12)};
  min-height: 62px;
  box-sizing: border-box;
`;

const DocumentName = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const DocumentRegistered = styled.span`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.primaryBlue[40]};
  white-space: nowrap;
`;

const DocumentRegisterButton = styled.button`
  ${theme.typography.contents.regular20};
  border: 2px solid #DFDFDF;
  border-radius: 4px;
  background: #ffffff;
  color: #676767;
  padding: 4px 20px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.grayScale[10]};
  }
`;

const AgreementIntro = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  white-space: pre-line;
`;

const AgreementBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwWeb(4)};
  background: ${theme.colors.grayScale[10]};
  padding: ${convertToVwWeb(20)};
  box-sizing: border-box;
`;

const AgreementItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(6)};
`;

const AgreementItemTitle = styled.p`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const AgreementItemBody = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  white-space: pre-line;
  line-height: 160%;
`;

const AgreementList = styled.ul`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  padding-left: ${convertToVwWeb(20)};
  line-height: 160%;
`;

const AgreementListItem = styled.li`
  margin: 0;
`;

const AgreementNote = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  margin: 0;
  white-space: pre-line;
  line-height: 160%;
`;

/* 나의 계정 화면과 같은 체크박스 스타일 */
const CheckboxGroup = styled.div`
  display: flex;
  gap: ${convertToVwWeb(12)};
  align-items: flex-start;
  cursor: pointer;
  min-width: 0;
`;

const CheckboxSquare = styled.div<{ active?: boolean }>`
  width: ${convertToVwWeb(20)};
  height: ${convertToVwWeb(20)};
  flex: 0 0 ${convertToVwWeb(20)};
  margin-top: ${convertToVwWeb(5)};
  border: 1px solid ${(props) => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[40])};
  border-radius: ${convertToVwWeb(2)};
  background-color: ${(props) => (props.active ? theme.colors.primaryBlue[40] : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.active &&
    `
    &::after {
      content: '✓';
      color: white;
      font-size: ${convertToVwWeb(14)};
    }
  `}
`;

const CheckboxLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 31px 29px;
  border: 1px solid ${theme.colors.grayScale[20]};
  background: ${theme.colors.grayScale[0]};
  border-radius: 0 0 ${convertToVwWeb(12)} ${convertToVwWeb(12)};
`;

export default GivingAccountApplyWeb;
